import {
  NavigationProp,
  useFocusEffect,
  useNavigation,
} from "@react-navigation/native";
import { BarCodeScanningResult } from "expo-camera";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useWindowDimensions } from "react-native";
import { CameraViewFinder } from "../../../../../../components";
import {
  BarcodeScannerRootParamList,
  ValidSetupRootParamList,
} from "../../../../../../types";
import {
  computeScannableArea,
  toOverlayPaddingPercentage,
} from "../../../../../../utilities/barcode-scanner/utils";
import { useDatabaseProductHelpers } from "../../../../../../utilities/product";
import { BarcodeScannerOverlay } from "../../../../components";
import { useFlashMessages } from "@towersystems/roam-common-native";
import { BarcodeScanner } from "../../../../../../components/BarcodeScanner/BarcodeScanner.web";
import { MaterialIcons } from "@expo/vector-icons";
import { QuaggaJSResultObject } from "@ericblade/quagga2";

export type BarcodeScannerScreenProps = {
  onScanSuccess: (productId: string) => void;
};

export const BarcodeScannerScreen = ({
  onScanSuccess,
}: BarcodeScannerScreenProps) => {
  const { showMessage } = useFlashMessages();

  const { width: screenWidth, height: screenHeight } = useWindowDimensions();

  const navigation =
    useNavigation<
      NavigationProp<ValidSetupRootParamList & BarcodeScannerRootParamList>
    >();

  const { navigate } = navigation;

  // used to stop scanning until screen regains focus
  const [isActive, setIsActive] = useState(false);

  const { findProductByStockNumber } = useDatabaseProductHelpers();

  const handleCancel = useCallback(() => {
    (navigation as any).goBack();
  }, []);

  const handleScanned = ({ codeResult }: QuaggaJSResultObject) => {
    setIsActive(false);

    const stockNumber = codeResult.code;

    if (!stockNumber) {
      setIsActive(true);
      return;
    }

    findProductByStockNumber(stockNumber)
      .then((res) => {
        if (!res) {
          // show not found screen
          navigate("ScanAgainScreen", {
            barcode: stockNumber,
          });
        } else {
          onScanSuccess(res.id);
        }
      })
      .catch(() => {
        showMessage({
          message: "Unexpected error occurred",
          severity: "error",
        });
        setIsActive(true);
      });
  };

  function handleSettingsPress() {
    navigate("BarcodeSettingsScreen");
  }

  useFocusEffect(
    useCallback(() => {
      setIsActive(true);

      return () => {
        setIsActive(false);
      };
    }, [])
  );

  const scannableArea = useMemo(
    () =>
      computeScannableArea({
        screenWidth,
        screenHeight,
      }),
    [screenWidth, screenHeight]
  );

  const decodeArea = useMemo(
    () => toOverlayPaddingPercentage(scannableArea, screenWidth, screenHeight),
    [scannableArea]
  );

  return (
    <BarcodeScanner
      active={isActive}
      scannableArea={scannableArea}
      headerProps={{
        showBackButton: false,
        rightComponent: (
          <CameraViewFinder.IconButton onPress={handleSettingsPress}>
            <MaterialIcons name="settings" size={24} color="white" />
          </CameraViewFinder.IconButton>
        ),
      }}
      overlayElement={
        <BarcodeScannerOverlay
          horizontalPadding={decodeArea.left}
          verticalPadding={decodeArea.top}
          onCancel={handleCancel}
        />
      }
      onBarCodeScanned={handleScanned}
    />
  );
};
